/*  MUI  */
import { Alert, Box, Grid, Paper, TableCell, Typography } from '@mui/material';

/*  Shared  */
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import { DataCard } from 'application/Shared/Components/Cards/Components/DataCard';
import { ManagedTable } from 'application/Shared/Components/Table';
import { Currency } from 'application/Shared/Components/Text';
import { LightTooltip } from 'application/Shared/Components/Tooltips';
import { FontSizes } from 'application/Shared/Constants';
import { Response, Route } from 'application/Shared/Helpers';
import { TenantApp } from 'application/Shared/Requests';
import Requests from 'application/Shared/Requests/Handler';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';

/*  Objects  */
import { Application, ApplicationBusinessOwner } from 'objects/Application';
import {
    Contract,
    ContractAlertingIconWithText,
    ContractDate,
    ContractTCV,
    ContractType
} from 'objects/Contract';

/*  React  */
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { withRouter } from 'router/withRouter';

const SingleCard = ({ title, content }) => (
    <DataCard title={title}>
        <Typography
            variant="h1"
            color="text.cardKPI"
            style={{
                alignItems: 'center',
                display: 'flex',
                fontSize: FontSizes[5],
                fontWeight: 500,
                minHeight: '80px'
            }}
        >
            {content}
        </Typography>
    </DataCard>
);

const searchFields = [
    'application.name',
    'application.category',
    'application.subCategory'
];

const getHeaders = ({ clientId, t }) => {
    const ownerColumnHeader = clientId
        ? t('Common.business_owner')
        : t('Common.clients');

    return [
        {
            align: 'left',
            field: 'name',
            renderHeader: <strong>{t('Common.application')}</strong>,
            sortable: true,
            sortableFieldType: 'string',
            sortableKey: 'application.name',
            tooltip: 'application',
            width: '25%'
        },
        {
            align: 'left',
            field: clientId ? 'business_owner' : 'clients',
            renderHeader: <strong>{ownerColumnHeader}</strong>,
            tooltip: clientId ? 'business_owner' : null,
            width: '15%'
        },
        {
            align: 'left',
            field: 'alerting',
            renderHeader: <strong>{t('Common.alerting')}</strong>,
            width: '10%'
        },
        {
            align: 'left',
            field: 'type',
            renderHeader: <strong>{t('Common.type')}</strong>,
            tooltip: 'contract_management_monthly_renewal_date',
            width: '20%'
        },
        {
            align: 'left',
            field: 'renewalDate',
            renderHeader: <strong>{t('Common.contract_date')}</strong>,
            tooltip: 'renewalDate',
            width: '15%'
        },
        {
            align: 'left',
            field: 'tcv',
            renderHeader: <strong>{t('Common.tcv')}</strong>,
            tooltip: 'contract_management_tcv',
            width: '15%'
        }
    ];
};

class ContractsPage extends Component {
    constructor(props) {
        super(props);
        this.state = {
            clientId: '',
            clientMap: {},
            contracts: [],
            headers: null,
            loaded: false,
            noticeLink: '',
            parameters: {},
            totals: {
                month: 0,
                mrr: 0,
                nrc: 0,
                renew: 0,
                tcv: 0
            }
        };

        const clientId = props?.match?.params?.clientId;
        /* requests */
        this.requests = new Requests();
        this.TenantApp = new TenantApp(clientId);
    }

    componentDidMount() {
        this.load();
    }

    componentWillUnmount() {
        this.unload();
    }

    load = async () => {
        const { match, partner, t } = this.props;
        const { clientId, partnerId } = match.params;
        const route = Route.buildBaseAdminRoute({ clientId, partnerId });

        let noticeLink = `${route}/applications`;
        const parameters = {
            includeApplication: true,
            size: -1,
            type: 'renew'
        };

        if (partner) {
            noticeLink = '/partner/applicationSettings';
            parameters.tenantMultiple = true;
        }

        this.setState(
            {
                clientId,
                headers: getHeaders({ clientId, t }),
                noticeLink,
                parameters
            },
            () => {
                this.loadContracts();
            }
        );
    };

    loadClients = async (applicationId) => {
        const { clientMap } = this.state;
        try {
            const tenantApps = await this.TenantApp.getAll({
                applicationId,
                includeClients: true,
                includeTenant: true
            });
            if (!tenantApps) {
                debug.e('No Tenant App Links Found', tenantApps);
                return;
            }

            const tenantStatsMap = tenantApps.reduce((acc, { tenant }) => {
                const clientNames = acc[applicationId] || {
                    names: []
                };
                if (!clientNames.names.includes(tenant.name)) {
                    clientNames.names.push(tenant.name);
                }
                return { ...acc, [applicationId]: clientNames };
            }, {});

            const currentMap = Object.assign(clientMap, tenantStatsMap);

            this.setState({ clientMap: currentMap });
        } catch (e) {
            /* */
        }
    };

    loadContracts = async () => {
        const { partner } = this.props;
        const { clientId } = this.state;

        const params = { includeApplication: true, size: -1, type: '*' };
        if (clientId) {
            params.platformTenantId = clientId;
            params.includeClients = true;
        }
        if (partner) {
            params.tenantMultiple = true;
        }

        try {
            const response = await axios.get('/contract', {
                cancelToken: this.requests.token(),
                params
            });
            if (!Response.hasData(response)) {
                this.setState({ loaded: true });
                return;
            }

            const contracts = Response.getData(response);
            this.setState({ contracts });
            this.makeTotals({ contracts });
        } catch (e) {
            debug.e('Unable to load Contracts', e);
        }
    };

    makeTotals = (p) => {
        const { contracts } = p;
        const totals = {
            month: 0,
            mrr: 0,
            nrc: 0,
            renew: 0,
            tcv: 0
        };

        Object.values(contracts).forEach((contract) => {
            if (!contract) {
                return;
            }

            const { mrr, nrc, renewalDate, tcv, type } = contract;
            if (
                (type === 'renew' &&
                    new Date(renewalDate).getTime() > new Date().getTime()) ||
                type === 'month'
            ) {
                totals.tcv += parseFloat(tcv);
                totals.mrr += parseFloat(mrr);
                totals.nrc += parseFloat(nrc);
            }
        });

        totals.month = contracts.filter((a) => a.type === 'month').length;
        totals.renew = contracts.filter((a) => a.type === 'renew').length;

        this.setState({ loaded: true, totals });
    };

    unload = () => {
        this.requests.cancel();
    };

    renderCards = () => {
        const { t } = this.props;
        const { contracts, totals } = this.state;

        return (
            <>
                <Grid item xs={12} sm={6} md={4}>
                    <SingleCard
                        title={t('Common.total_contracts')}
                        content={contracts.length}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <SingleCard
                        title={t('Common.monthly_contracts')}
                        content={totals.month}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <SingleCard
                        title={t('Common.renewal_contracts')}
                        content={totals.renew}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <SingleCard
                        title={t('Common.tcv_full')}
                        content={<Currency currency={totals.tcv} />}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <SingleCard
                        title={t('Common.mrr')}
                        content={<Currency currency={totals.mrr} />}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <SingleCard
                        title={t('Common.nrc')}
                        content={<Currency currency={totals.nrc} />}
                    />
                </Grid>
            </>
        );
    };

    renderNotice = () => {
        const { t } = this.props;
        const { noticeLink } = this.state;

        return (
            <Grid item xs={12}>
                <Alert severity="info" icon={false}>
                    {t('Notice.no_contracts.part1')}
                    <Link
                        to={noticeLink}
                        data-trackid="contracts_noticeLink_routeTo"
                    >
                        {t('Common.applications')}
                    </Link>
                    {t('Notice.no_contracts.part2')}
                </Alert>
            </Grid>
        );
    };

    renderClients = (row) => {
        const { clientMap } = this.state;

        const numberOfClients = clientMap[row.applicationId]?.names?.length;

        if (!numberOfClients) {
            return null;
        }

        if (numberOfClients === 1) {
            return (
                <Typography
                    variant="body2"
                    style={{
                        marginBottom: '4px'
                    }}
                >
                    {clientMap[row.applicationId].names[0]}
                </Typography>
            );
        }

        const sortedClients = clientMap[row.applicationId].names.sort();
        const nameToDisplay = `${sortedClients[0]}, ...`;

        return (
            <LightTooltip
                title={
                    <Box maxHeight="160px" overflow="auto">
                        {sortedClients.map((client) => (
                            <Typography
                                variant="body2"
                                key={`renderClientTooltip_${row.applicationId}_${client}`}
                                style={{
                                    fontSize: FontSizes[0],
                                    marginBottom: '4px'
                                }}
                            >
                                {client}
                            </Typography>
                        ))}
                    </Box>
                }
                arrow
                placement="right"
            >
                <Typography
                    variant="body2"
                    style={{
                        marginBottom: '4px'
                    }}
                >
                    {nameToDisplay}
                </Typography>
            </LightTooltip>
        );
    };

    renderBody = (row) => {
        const { match, t } = this.props;
        const { partnerId, clientId } = match.params;
        const basePartnerRoute = Route.buildBasePartnerRoute({
            partnerId
        });

        return (
            <>
                <Application
                    data={row.application}
                    fromPage={t('ContractManagement.title')}
                    nameHasLink={clientId}
                    nameLinkTo={`${basePartnerRoute}/applicationSettings`}
                >
                    <TableCell>
                        <ApplicationNameLinkCell withCategory />
                    </TableCell>
                    <TableCell>
                        {clientId ? (
                            <ApplicationBusinessOwner />
                        ) : (
                            this.renderClients(row)
                        )}
                    </TableCell>
                </Application>
                <Contract data={row}>
                    <TableCell>
                        <ContractAlertingIconWithText />
                    </TableCell>
                    <TableCell>
                        <ContractType />
                    </TableCell>
                    <TableCell>
                        <ContractDate />
                    </TableCell>
                    <TableCell>
                        <ContractTCV />
                    </TableCell>
                </Contract>
            </>
        );
    };

    handleRows = (rows) => {
        if (!rows?.length) {
            return;
        }

        try {
            rows.forEach(async (row) => {
                const { applicationId } = row;
                await this.loadClients(applicationId);
            });
        } catch (e) {
            debug.e(e);
        }
    };

    renderTable = () => {
        const { headers, parameters } = this.state;

        return (
            <ManagedTable
                handleRows={this.handleRows}
                headers={headers}
                parameters={parameters}
                renderBody={this.renderBody}
                renderBodyKey="id"
                renderFooter
                rowsApi="/contract"
                rowsApiPreload
                rowsPerPage={10}
                searchEnabled
                searchFields={searchFields}
                sortDirection="ASC"
                sortFieldType="date"
                sortKey="renewalDate"
            />
        );
    };

    render() {
        const { t } = this.props;
        const { loaded, contracts } = this.state;

        if (!loaded) {
            return null;
        }

        return (
            <Grid
                container
                spacing={3}
                alignItems="stretch"
                data-testid="sharedPagesContracts"
            >
                {!contracts.length && this.renderNotice()}
                {this.renderCards()}
                <Grid item xs={12}>
                    <Paper elevation={0} variant="outlined">
                        <Typography variant="h4">
                            {t('Common.renewal_contracts')}
                        </Typography>
                        {this.renderTable()}
                    </Paper>
                </Grid>
            </Grid>
        );
    }
}

export default withRouter(withTranslation()(ContractsPage));
